import { useCallback, useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import useDebounce from 'Hooks/useDebounce';
import employeesApi from 'Api/axios/employees-api';
import type { Users, UsersQuery, PaginationMetaI } from 'types';

interface FormDefaults {
  search: string;
  terminal: string;
  department: string;
  status: string;
}

const defaultValues: FormDefaults = {
  search: '',
  terminal: '',
  department: '',
  status: '',
};

const useEmployeePage = () => {
  const { control, watch } = useForm<FormDefaults>({
    defaultValues,
  });

  const [rows, setRows] = useState<Users['data']>([]);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState<PaginationMetaI | null>(null);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(15);

  const searchValue = watch('search');
  const terminalValue = watch('terminal');
  const departmentValue = watch('department');
  const statusValue = watch('status');

  const debouncedSearch = useDebounce(searchValue, 500);

  useEffect(() => {
    const fetchEmployees = async () => {
      setLoading(true);
      const params: UsersQuery = {
        page: page + 1,
        pageSize,
      };

      if (debouncedSearch) params.name = debouncedSearch;
      if (terminalValue) params.terminalId = terminalValue;
      if (departmentValue) params.departmentId = departmentValue;
      if (statusValue) params.status = statusValue === 'active' ? 'true' : 'false';

      const response = await employeesApi.getUsers(params);

      if (response.data) {
        setRows(response.data);
      }
      if (response.pagination) {
        setPagination(response.pagination);
      }
      setLoading(false);
    };

    fetchEmployees();
  }, [debouncedSearch, terminalValue, departmentValue, statusValue, page, pageSize]);

  const toggleStatus = useCallback(async (userId: string, currentStatus: boolean) => {
    const newStatus = !currentStatus;
    const response = await employeesApi.updateStatus(userId, newStatus);
    if (response.data) {
      setRows(prev => prev.map(row => (row.id === userId ? { ...row, status: newStatus } : row)));
    }
  }, []);

  return {
    control,
    rows,
    loading,
    pagination,
    page,
    pageSize,
    setPage,
    setPageSize,
    toggleStatus,
  };
};

export default useEmployeePage;
